import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { store } from '../root/ReduxStore.store';
import { PaymentDetailsState } from './payment-model';
import { OfflineCollectPaymentRequest } from '../offline';

export const PAYMENT_FEATURE_SLICE = 'paymentData';

interface PaymentDataState {
  paymentCustomerInfo: {
    paymentRefCode?: string;
    customerName?: string;
    status?: string;
    createdBy?: string;
    inviteId?: string;
    id?: string;
  };
  paymentEditData: PaymentDetailsState;
  collectPaymentSuccess: {
    showSuccess: boolean;
    amount: number;
    customerName: string;
    mode: string;
  };
  offlinePaymentDetails: OfflineCollectPaymentRequest;
}

const initialPaymentDataState: PaymentDataState = {
  paymentCustomerInfo: {},
  paymentEditData: {
    paymentDetails: [],
    paymentMedia: [],
    paymentInvoice: [],
  },
  collectPaymentSuccess: {
    showSuccess: false,
    amount: 0,
    customerName: '',
    mode: '',
  },
  offlinePaymentDetails: {
    data: {
      sellerWorkspaceId: '',
      inviteId: '',
      paymentDate: '',
      amountPaid: 0,
      doctype: '',
    },
    media: [],
  },
};

export const paymentSlice = createSlice({
  name: PAYMENT_FEATURE_SLICE,
  initialState: initialPaymentDataState,
  reducers: {
    updatePaymentCustomerInfo: (state, action: PayloadAction<any>) => {
      state.paymentCustomerInfo = {
        ...state.paymentCustomerInfo,
        ...action.payload,
      };
    },
    updateEditPaymentDetails: (
      state,
      action: PayloadAction<PaymentDetailsState>
    ) => {
      state.paymentEditData = action.payload;
    },
    clearEditPaymentDetails: (state) => {
      state.paymentEditData = initialPaymentDataState.paymentEditData;
    },
    clearPaymentCustomerInfo: (state) => {
      state.paymentCustomerInfo = initialPaymentDataState.paymentCustomerInfo;
    },
    updateCollectPaymentSuccess: (
      state,
      action: PayloadAction<{
        showSuccess: boolean;
        amount: number;
        customerName: string;
        mode: string;
      }>
    ) => {
      state.collectPaymentSuccess = action.payload;
    },
    clearCollectPaymentSuccess: (state) => {
      state.collectPaymentSuccess =
        initialPaymentDataState.collectPaymentSuccess;
    },
    updateOfflinePaymentDetails: (
      state,
      action: PayloadAction<OfflineCollectPaymentRequest>
    ) => {
      state.offlinePaymentDetails = action.payload;
    },
    clearOfflinePaymentDetails: (state) => {
      state.offlinePaymentDetails =
        initialPaymentDataState.offlinePaymentDetails;
    },
  },
});

export const PaymentDataReducer = paymentSlice.reducer;
export const {
  updatePaymentCustomerInfo,
  updateEditPaymentDetails,
  clearEditPaymentDetails,
  clearPaymentCustomerInfo,
  updateCollectPaymentSuccess,
  clearCollectPaymentSuccess,
  updateOfflinePaymentDetails,
  clearOfflinePaymentDetails,
} = paymentSlice.actions;

export const getPaymentDataSliceState = (
  rootState: ReturnType<typeof store.getState>
): PaymentDataState => rootState[PAYMENT_FEATURE_SLICE];

export const getPaymentCustomerInfo = createSelector(
  getPaymentDataSliceState,
  (state) => state.paymentCustomerInfo
);

export const getEditPaymentDetails = createSelector(
  getPaymentDataSliceState,
  (state) => state.paymentEditData
);

export const getCollectPaymentSuccess = createSelector(
  getPaymentDataSliceState,
  (state) => state.collectPaymentSuccess
);

export const getOfflinePaymentDetails = createSelector(
  getPaymentDataSliceState,
  (state) => state.offlinePaymentDetails
);
